'use client'

import {SupportContactForm} from '@/app/[locale]/support/SupportContactForm'
import {Button} from '@/components/ui/button'
import {ConeStarlikeSunburstIcon} from '@/components/ui/icons/graphical-elements'
import {MailIcon} from '@/components/ui/icons/MailIcon'
import {TopSectionWithIcon} from '@/components/ui/TopSectionWithIcon'
import {Display, Text} from '@/components/ui/Typography'
import {useTranslations} from '@/i18n/hooks/useTranslations'
import {Link} from '@/i18n/navigation'
import {MinusIcon, PlusIcon} from 'lucide-react'
import React, {useState} from 'react'

export const Support: React.FC<{locale?: string}> = ({locale}) => {
  const t = useTranslations()
  const supportEmail = t('contact.supportEmail')
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <main>
      <TopSectionWithIcon
        title={t('contact.title')}
        subTitle={t('contact.subTitle')}
        icon={<ConeStarlikeSunburstIcon className="w-80 h-80" />}
        bgColor={'bg-piffl-pink-500'}
        titleColor={'text-white'}
      />
      <section>
        <div className="container mx-auto grid md:grid-cols-2 gap-20 mb-20">
          <div>
            <Display className="mb-2">{t('contact.supportTitle')}</Display>
            <Text className="mb-6">{t('contact.supportDescription')}</Text>
            <Display className="text-2xl">{t('contact.faqTitle')}</Display>
            <ul className="list-none divide-y divide-gray-200">
              {t
                .raw('faq.questions')
                .slice(0, 6)
                .map((faq: {question: string; answer: string}, index: number) => (
                  <li key={index} className="py-4">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="flex items-center w-full text-left text-lg font-medium text-[#334B55] hover:text-[#334B55]/80 transition-colors"
                    >
                      <span className={'mr-2'}>
                        {openIndex === index ? (
                          <MinusIcon className="h-5 w-5 text-accent-kettlebell flex-shrink-0" />
                        ) : (
                          <PlusIcon className="h-5 w-5 text-accent-kettlebell flex-shrink-0" />
                        )}
                      </span>
                      <Text>{faq.question}</Text>
                    </button>
                    {openIndex === index && <Text className="mt-2 text-gray-600">{faq.answer}</Text>}
                  </li>
                ))}
            </ul>
            <div className="flex justify-center my-8">
              <Button asChild variant="secondary">
                <Link href="/faq">{t('contact.viewAllButton')}</Link>
              </Button>
            </div>
          </div>
          <div>
            <Display className="mb-2">{t('contact.contactTitle')}</Display>
            <Text className="mb-6">{t('contact.contactDescription')}</Text>
            <SupportContactForm locale={locale} />
            <div className={'flex justify-center'}>
              <div className="flex flex-col md:flex-row md:items-center">
                <Text className="mb-1 flex items-center gap-2">
                  <MailIcon className="w-6 h-6 text-court-600" />
                  <a className="font-bold text-court-600" href={`mailto:${supportEmail}`}>
                    {supportEmail}
                  </a>
                </Text>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
